import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useUtmTaxonomyStore } from '@/store/utm-taxonomy-store';
import type { Zone, Impact, Category, Group } from '@/store/utm-taxonomy-store';
import { Settings, Target, ArrowLeft, PanelLeftClose, X } from 'lucide-react';
import dynamicIconImports from 'lucide-react/dynamicIconImports';
import { Icon, type IconName } from '@/components/utm/shared/UtmIconPicker';
import * as turf from '@turf/turf';
import { calculateArea, calculateRadius } from '@/utils/utm-geometry';
import { buildUtmApiUrl } from '../shared/utm-backend-url';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TabType = 'zones' | 'criterias';

type IconSource = Pick<Category, 'iconType' | 'iconValue' | 'iconColor' | 'iconBgColor'>;

// ---------------------------------------------------------------------------
// Checklist row sub-components
// ---------------------------------------------------------------------------

interface RowCheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  visualOnly?: boolean;
}

const RowCheckbox = ({
  checked,
  indeterminate = false,
  onCheckedChange,
  visualOnly = false,
}: RowCheckboxProps) => (
  <input
    ref={(node) => {
      if (node) {
        node.indeterminate = indeterminate && !checked;
      }
    }}
    type="checkbox"
    checked={checked}
    onChange={(e) => onCheckedChange?.(e.target.checked)}
    onClick={(e) => e.stopPropagation()}
    disabled={visualOnly}
    readOnly={!onCheckedChange}
    tabIndex={visualOnly ? -1 : 0}
    className="h-4 w-4 shrink-0 rounded border border-[#8f8f8f] bg-white align-middle accent-[#2d2d2d] disabled:cursor-default disabled:opacity-100"
  />
);

interface ChecklistRowProps {
  depth: number;
  label: string;
  meta?: string;
  icon?: ReactNode;
  checked: boolean;
  indeterminate?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  onClick?: () => void;
  visualOnly?: boolean;
  active?: boolean;
}

const ChecklistRow = ({
  depth,
  label,
  meta,
  icon,
  checked,
  indeterminate = false,
  onCheckedChange,
  onClick,
  visualOnly = false,
  active = false,
}: ChecklistRowProps) => {
  const isClickable = Boolean(onClick);

  return (
    <div
      className={`flex min-w-0 items-center gap-2 rounded-sm px-1 py-1 text-[13px] transition-colors ${
        isClickable ? 'hover:bg-black/5' : ''
      }`}
      style={{ marginLeft: depth * 14 }}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : -1}
      onKeyDown={(e) => {
        if (!isClickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <RowCheckbox
        checked={checked}
        indeterminate={indeterminate}
        onCheckedChange={onCheckedChange}
        visualOnly={visualOnly}
      />

      {icon ?? <span className="h-4 w-4 rounded-sm border border-border bg-muted" />}

      <span className="min-w-0 flex-1 truncate text-[13px] leading-5 text-[#3a3a3a]">{label}</span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface UtmSidebarProps {
  hidden?: boolean;
  onToggleHidden?: () => void;
  showHideToggle?: boolean;
  className?: string;
}

const UtmSidebar = ({
  hidden = false,
  onToggleHidden,
  showHideToggle = true,
  className = '',
}: UtmSidebarProps) => {
  const {
    categories,
    groups,
    activeGroups,
    fetchTaxonomy,
    toggleGroup,
    selectionResults,
    selectZone,
    selectionMode,
    currentDrawingGeometry,
    selectionFeatures,
    activeSelectionFeatureId,
    drawingElevation,
    corridorWidth,
    setDrawingElevation,
    setCorridorWidth,
    clearSelection,
    selectedZone,
  } = useUtmTaxonomyStore();

  const [activeTab, setActiveTab] = useState<TabType>('zones');
  const [showTabs, setShowTabs] = useState(false);

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  useEffect(() => {
    fetchTaxonomy();
  }, [fetchTaxonomy]);

  useEffect(() => {
    if (selectionResults) {
      const nextTick = window.setTimeout(() => {
        setShowTabs(true);
        setActiveTab('zones');
      }, 0);

      return () => {
        window.clearTimeout(nextTick);
      };
    }
  }, [selectionResults]);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const groupsById = useMemo(() => {
    return new Map(groups.map((group) => [group.id, group]));
  }, [groups]);

  const groupsByCategoryId = useMemo(() => {
    return groups.reduce<Map<string, Group[]>>((acc, group) => {
      if (!acc.has(group.categoryId)) {
        acc.set(group.categoryId, []);
      }
      acc.get(group.categoryId)!.push(group);
      return acc;
    }, new Map());
  }, [groups]);

  const getS3IconUrl = (iconValue?: string) => {
    if (!iconValue) return null;
    if (iconValue.startsWith('http://') || iconValue.startsWith('https://')) {
      return iconValue;
    }
    return buildUtmApiUrl(`/files/serve-by-key/${encodeURIComponent(iconValue)}?bucket=public`);
  };

  const renderConfiguredIcon = (item?: IconSource, altText?: string) => {
    if (!item?.iconValue) {
      return <span className="h-4 w-4 rounded-sm border border-border bg-muted" />;
    }

    if (item.iconType === 'BUILTIN' && item.iconValue in dynamicIconImports) {
      return (
        <span
          className="flex h-4 w-4 items-center justify-center rounded-sm"
          style={{ backgroundColor: item.iconBgColor || '#1e293b' }}
        >
          <Icon
            name={item.iconValue as IconName}
            size={10}
            style={{ color: item.iconColor || '#fff' }}
          />
        </span>
      );
    }

    if (item.iconType === 'S3_OBJECT') {
      const url = getS3IconUrl(item.iconValue);
      if (url) {
        return (
          <span
            className="flex h-4 w-4 items-center justify-center overflow-hidden rounded-sm"
            style={{ backgroundColor: item.iconBgColor || '#1e293b' }}
          >
            <img src={url} alt={altText || 'icon'} className="h-3 w-3 object-contain" />
          </span>
        );
      }
    }

    return <span className="h-4 w-4 rounded-sm border border-border bg-muted" />;
  };

  // -------------------------------------------------------------------------
  // Formatters
  // -------------------------------------------------------------------------

  const formatArea = (area: number) => {
    if (area >= 1_000_000) {
      return `${(area / 1_000_000).toFixed(2)} km²`;
    }
    return `${area.toFixed(2)} m²`;
  };

  const formatRadius = (radius: number | null) => {
    if (radius === null) return 'N/A';
    if (radius >= 1000) {
      return `${(radius / 1000).toFixed(2)} km`;
    }
    return `${radius.toFixed(2)} m`;
  };

  const formatDateTime = (value?: string) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const formatLength = (meters: number) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`;
    }
    return `${meters.toFixed(2)} m`;
  };

  // -------------------------------------------------------------------------
  // Computed zone / impact categorization
  // -------------------------------------------------------------------------

  const zoneCategorization = useMemo(() => {
    if (!selectionResults || selectionResults.zones.length === 0) return null;

    const categoriesMap = new Map<
      string,
      {
        category: (typeof categories)[0];
        groups: Map<
          string,
          {
            group: (typeof groups)[0];
            zones: Zone[];
          }
        >;
        totalZones: number;
      }
    >();

    selectionResults.zones.forEach((zone) => {
      const group = groups.find((g) => g.id === zone.groupId);
      if (!group) return;

      const category = categories.find((c) => c.id === group.categoryId);
      if (!category) return;

      if (!categoriesMap.has(category.id)) {
        categoriesMap.set(category.id, {
          category,
          groups: new Map(),
          totalZones: 0,
        });
      }

      const catData = categoriesMap.get(category.id)!;

      if (!catData.groups.has(group.id)) {
        catData.groups.set(group.id, { group, zones: [] });
      }

      catData.groups.get(group.id)!.zones.push(zone);
      catData.totalZones++;
    });

    return categoriesMap;
  }, [selectionResults, groups, categories]);

  const impactCategorization = useMemo(() => {
    if (!selectionResults || selectionResults.impacts.length === 0) return null;

    const impactsMap = new Map<
      string,
      {
        impact: Impact;
        zones: Array<{
          id: string;
          zoneCode: string;
          zoneName: string;
          groupId: string;
          group?: {
            id: string;
            code: string;
            name: string;
          };
        }>;
      }
    >();

    selectionResults.impacts.forEach((impact) => {
      impactsMap.set(impact.id, {
        impact,
        zones: impact.triggerZones || [],
      });
    });

    return impactsMap;
  }, [selectionResults]);

  const activeSelectionGeometry = useMemo(() => {
    if (!activeSelectionFeatureId) return null;
    const feature = selectionFeatures.find((entry) => entry.id === activeSelectionFeatureId);
    return feature?.geometry ?? null;
  }, [selectionFeatures, activeSelectionFeatureId]);

  const selectedDrawingArea = useMemo(() => {
    if (!activeSelectionGeometry) return 0;
    return calculateArea(activeSelectionGeometry);
  }, [activeSelectionGeometry]);

  const selectedDrawingRadius = useMemo(() => {
    if (!activeSelectionGeometry) return null;
    return calculateRadius(activeSelectionGeometry);
  }, [activeSelectionGeometry]);

  // Geometry metrics for the selected zone detail panel
  const selectedZoneMetrics = useMemo(() => {
    if (!selectedZone) return null;

    const zoneAny = selectedZone as any;
    const geometry = zoneAny?.geometry?.geom || zoneAny?.geometry;
    if (!geometry || !geometry.type) return null;

    try {
      const feature = turf.feature(geometry as any);

      if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
        return {
          geometryType: geometry.type,
          primaryLabel: 'Area',
          primaryValue: formatArea(turf.area(feature)),
        };
      }

      if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
        const lengthM = turf.length(feature, { units: 'kilometers' }) * 1000;
        return {
          geometryType: geometry.type,
          primaryLabel: 'Length',
          primaryValue: formatLength(lengthM),
        };
      }

      if (geometry.type === 'Point') {
        const [lon, lat] = geometry.coordinates || [];
        return {
          geometryType: geometry.type,
          primaryLabel: 'Coordinates',
          primaryValue:
            Number.isFinite(lat) && Number.isFinite(lon)
              ? `${lat.toFixed(6)}, ${lon.toFixed(6)}`
              : 'N/A',
        };
      }

      if (geometry.type === 'MultiPoint') {
        return {
          geometryType: geometry.type,
          primaryLabel: 'Point Count',
          primaryValue: `${geometry.coordinates?.length || 0}`,
        };
      }

      if (geometry.type === 'GeometryCollection') {
        let totalArea = 0;
        let totalLengthM = 0;
        (geometry.geometries || []).forEach((g: any) => {
          const gFeature = turf.feature(g);
          if (g?.type === 'Polygon' || g?.type === 'MultiPolygon') {
            totalArea += turf.area(gFeature);
          }
          if (g?.type === 'LineString' || g?.type === 'MultiLineString') {
            totalLengthM += turf.length(gFeature, { units: 'kilometers' }) * 1000;
          }
        });

        return {
          geometryType: geometry.type,
          primaryLabel: 'Metrics',
          primaryValue:
            `${totalArea > 0 ? `Area ${formatArea(totalArea)}` : ''}${totalArea > 0 && totalLengthM > 0 ? ' • ' : ''}${totalLengthM > 0 ? `Length ${formatLength(totalLengthM)}` : ''}` ||
            'N/A',
        };
      }
    } catch (error) {
      console.error('Failed to compute zone geometry metrics:', error);
    }

    return {
      geometryType: geometry.type || 'Unknown',
      primaryLabel: 'Metrics',
      primaryValue: 'N/A',
    };
  }, [selectedZone]);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  const isDrawing = selectionMode !== 'none' && currentDrawingGeometry;

  const handleBackToFolders = () => {
    setShowTabs(false);
  };

  const toggleCategoryGroups = (categoryId: string, checked: boolean) => {
    const categoryGroups = groupsByCategoryId.get(categoryId) || [];
    categoryGroups.forEach((group) => toggleGroup(group.code, checked));
  };

  // -------------------------------------------------------------------------
  // Section renderers
  // -------------------------------------------------------------------------

  const renderFolderView = () => (
    <div className="space-y-1">
      {categories.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border py-8 text-center">
          <span className="mx-auto mb-2 block h-6 w-6 rounded border border-border bg-muted" />
          <p className="text-xs text-muted-foreground">No categories available</p>
        </div>
      ) : (
        categories.map((category) => {
          const categoryGroups = groupsByCategoryId.get(category.id) || [];
          const activeCount = categoryGroups.filter((group) => activeGroups.has(group.code)).length;
          const allActive = categoryGroups.length > 0 && activeCount === categoryGroups.length;
          const isIndeterminate = activeCount > 0 && activeCount < categoryGroups.length;

          return (
            <div key={category.id} className="rounded-md bg-white p-1">
              <ChecklistRow
                depth={0}
                label={category.name}
                checked={allActive}
                indeterminate={isIndeterminate}
                onCheckedChange={(checked) => toggleCategoryGroups(category.id, checked)}
                icon={renderConfiguredIcon(category, category.name)}
              />

              {categoryGroups.length === 0 ? (
                <div className="px-6 py-1 text-xs italic text-muted-foreground">No groups in this category</div>
              ) : (
                categoryGroups.map((group) => {
                  const isActive = activeGroups.has(group.code);

                  return (
                    <ChecklistRow
                      key={group.id}
                      depth={1}
                      label={group.name}
                      checked={isActive}
                      onCheckedChange={(checked) => toggleGroup(group.code, checked)}
                      icon={renderConfiguredIcon(group, group.name)}
                      active={isActive}
                    />
                  );
                })
              )}
            </div>
          );
        })
      )}
    </div>
  );

  const renderZonesTab = () => {
    if (!zoneCategorization) {
      return (
        <div className="rounded-xl border-2 border-dashed border-border py-8 text-center">
          <Target className="mx-auto mb-2 text-muted-foreground" size={24} />
          <p className="text-xs text-muted-foreground">No zones selected</p>
          <p className="mt-1 text-[10px] text-muted-foreground">Draw a polygon or circle on the map</p>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {Array.from(zoneCategorization.entries()).map(([catId, catData]) => (
          <div key={catId} className="rounded-md bg-white p-1">
            <ChecklistRow
              depth={0}
              label={catData.category.name}
              checked
              visualOnly
              icon={renderConfiguredIcon(catData.category, catData.category.name)}
            />

            {Array.from(catData.groups.entries()).map(([groupId, groupData]) => (
              <div key={groupId}>
                <ChecklistRow
                  depth={1}
                  label={groupData.group.name}
                  checked
                  visualOnly
                  icon={renderConfiguredIcon(groupData.group, groupData.group.name)}
                />

                {groupData.zones.map((zone) => (
                  <ChecklistRow
                    key={zone.id}
                    depth={2}
                    label={zone.zoneName}
                    checked
                    visualOnly
                    onClick={() => selectZone(zone.id)}
                    active={selectedZone?.id === zone.id}
                    icon={<span className="h-2 w-2 rounded-full bg-blue-500" />}
                  />
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderCriteriasTab = () => {
    if (!impactCategorization || impactCategorization.size === 0) {
      return (
        <div className="rounded-xl border-2 border-dashed border-border py-8 text-center">
          <Target className="mx-auto mb-2 text-muted-foreground" size={24} />
          <p className="text-xs text-muted-foreground">No impacts found</p>
          <p className="mt-1 text-[10px] text-muted-foreground">No impacts for this selection</p>
        </div>
      );
    }

    const byLevel: Record<
      string,
      Array<
        [
          string,
          {
            impact: Impact;
            zones: Array<{
              id: string;
              zoneCode: string;
              zoneName: string;
              groupId: string;
              group?: {
                id: string;
                code: string;
                name: string;
              };
            }>;
          },
        ]
      >
    > = {
      NOTAM: [],
      AUTHORISATION: [],
      NOTIFICATION: [],
      NOTHING_TO_DO: [],
    };

    Array.from(impactCategorization.entries()).forEach((entry) => {
      const level = entry[1].impact.level;
      if (byLevel[level]) {
        byLevel[level].push(entry);
      }
    });

    const levelColors = {
      NOTAM: 'bg-red-600',
      AUTHORISATION: 'bg-red-500',
      NOTIFICATION: 'bg-yellow-500',
      NOTHING_TO_DO: 'bg-green-500',
    };

    const levelLabels = {
      NOTAM: 'NOTAM',
      AUTHORISATION: 'Authorisation Required',
      NOTIFICATION: 'Notification Required',
      NOTHING_TO_DO: 'Informational',
    };

    return (
      <div className="space-y-1">
        {Object.entries(byLevel).map(([level, impacts]) => {
          if (impacts.length === 0) return null;

          return (
            <div key={level} className="rounded-md bg-white p-1">
              <ChecklistRow
                depth={0}
                label={levelLabels[level as keyof typeof levelLabels]}
                checked
                visualOnly
                icon={
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${levelColors[level as keyof typeof levelColors]}`}
                  />
                }
              />

              {impacts.map(([impactId, impactData]) => (
                <div key={impactId}>
                  <ChecklistRow
                    depth={1}
                    label={impactData.impact.name}
                    checked
                    visualOnly
                    icon={<span className="h-2 w-2 rounded-full bg-muted-foreground" />}
                  />

                  {impactData.zones.map((zone) => {
                    const zoneGroup = zone.group || groupsById.get(zone.groupId);
                    return (
                      <ChecklistRow
                        key={zone.id}
                        depth={2}
                        label={zone.zoneName}
                        checked
                        visualOnly
                        onClick={() => selectZone(zone.id)}
                        active={selectedZone?.id === zone.id}
                        icon={
                          zoneGroup ? (
                            renderConfiguredIcon(zoneGroup as Group, zone.zoneName)
                          ) : (
                            <span className="h-2 w-2 rounded-full bg-muted-foreground" />
                          )
                        }
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (hidden) {
    return null;
  }

  return (
    <div className={`z-20 flex h-full w-full max-w-[20rem] flex-col bg-white sm:max-w-[22rem] lg:w-80 lg:max-w-none ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-black/10 p-3 sm:p-4">
        <div>
          <h1 className="text-base font-semibold leading-tight text-[#2f2f2f]">MyMaps</h1>
        </div>
        {showHideToggle && (
          <button
            type="button"
            onClick={onToggleHidden}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-[#4a4a4a] transition-colors hover:bg-black/5"
            aria-label="Hide sidebar"
          >
            <PanelLeftClose size={14} />
            Hide
          </button>
        )}
      </div>

      {/* Selected zone detail panel */}
      {selectedZone && (
        <div className="border-b border-border bg-card p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Selected Zone</h3>
            <button
              type="button"
              onClick={() => selectZone(null)}
              className="rounded p-1 text-muted-foreground transition hover:bg-accent hover:text-foreground"
              aria-label="Close selected zone details"
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
          <div className="space-y-2 text-xs">
            <div>
              <div className="text-muted-foreground">Name</div>
              <div className="text-foreground">{selectedZone.zoneName || 'N/A'}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Code</div>
              <div className="text-foreground">{selectedZone.zoneCode || 'N/A'}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Status</div>
              <div className="text-foreground">{selectedZone.status || 'N/A'}</div>
            </div>
            {selectedZoneMetrics && (
              <>
                <div>
                  <div className="text-muted-foreground">Geometry Type</div>
                  <div className="text-foreground">{selectedZoneMetrics.geometryType}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">{selectedZoneMetrics.primaryLabel}</div>
                  <div className="text-foreground">{selectedZoneMetrics.primaryValue}</div>
                </div>
              </>
            )}
            <div>
              <div className="text-muted-foreground">Group</div>
              <div className="text-foreground">
                {(selectedZone as any).group?.name ||
                  groupsById.get(selectedZone.groupId)?.name ||
                  'N/A'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Active From</div>
              <div className="text-foreground">{formatDateTime(selectedZone.activeStart)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Active Until</div>
              <div className="text-foreground">{formatDateTime(selectedZone.activeEnd)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Lower Altitude</div>
              <div className="text-foreground">
                {(selectedZone as any).geometry?.lowerBoundM !== undefined ||
                (selectedZone as any).lowerBoundM !== undefined
                  ? `${(selectedZone as any).geometry?.lowerBoundM ?? (selectedZone as any).lowerBoundM} ${(selectedZone as any).geometry?.lowerRef ?? (selectedZone as any).lowerRef ?? 'AGL'}`
                  : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Upper Altitude</div>
              <div className="text-foreground">
                {(selectedZone as any).geometry?.upperBoundM !== undefined ||
                (selectedZone as any).upperBoundM !== undefined
                  ? `${(selectedZone as any).geometry?.upperBoundM ?? (selectedZone as any).upperBoundM} ${(selectedZone as any).geometry?.upperRef ?? (selectedZone as any).upperRef ?? 'AGL'}`
                  : 'N/A'}
              </div>
            </div>
            {selectedZone.description && (
              <div>
                <div className="text-muted-foreground">Description</div>
                <div className="text-foreground">{selectedZone.description}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs header (shown after a spatial query returns results) */}
      {showTabs && (
        <>
          <div className="px-4 pt-4">
            <button
              onClick={handleBackToFolders}
              className="flex w-full items-center space-x-2 rounded-lg px-3 py-2 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <ArrowLeft size={14} />
              <span>Back to Layers</span>
            </button>
          </div>

          {/* Drawing / selection info panel */}
          {isDrawing && (
            <div className="max-h-96 overflow-y-auto border-b border-border bg-card">
              <div className="p-4">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Zone Information</h3>

                <div className="mb-3">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Selected Area
                  </label>
                  <div className="text-sm font-semibold text-foreground">
                    {formatArea(selectedDrawingArea)}
                  </div>
                </div>

                {selectionMode === 'radius' && selectedDrawingRadius !== null && (
                  <div className="mb-3">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">Radius</label>
                    <div className="text-sm font-semibold text-foreground">
                      {formatRadius(selectedDrawingRadius)}
                    </div>
                  </div>
                )}

                {selectionMode === 'corridor' && (
                  <div className="mb-3">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                      Corridor Width (m)
                    </label>
                    <input
                      type="number"
                      min="10"
                      step="10"
                      value={corridorWidth}
                      onChange={(e) => setCorridorWidth(Number(e.target.value))}
                      className="w-full rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                )}

                <div className="mt-3 border-t border-border pt-3">
                  <div className="mb-2 text-xs font-semibold text-foreground">Elevation Bounds</div>

                  <div className="mb-2">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                      Lower Bound
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Meters"
                        value={drawingElevation.lowerBoundM ?? ''}
                        onChange={(e) =>
                          setDrawingElevation({
                            lowerBoundM: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className="flex-1 rounded border border-border bg-muted px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <select
                        value={drawingElevation.lowerRef ?? 'AGL'}
                        onChange={(e) =>
                          setDrawingElevation({ lowerRef: e.target.value as 'AGL' | 'AMSL' })
                        }
                        className="rounded border border-border bg-muted px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="AGL">AGL</option>
                        <option value="AMSL">AMSL</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">
                      Upper Bound
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Meters"
                        value={drawingElevation.upperBoundM ?? ''}
                        onChange={(e) =>
                          setDrawingElevation({
                            upperBoundM: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className="flex-1 rounded border border-border bg-muted px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <select
                        value={drawingElevation.upperRef ?? 'AGL'}
                        onChange={(e) =>
                          setDrawingElevation({ upperRef: e.target.value as 'AGL' | 'AMSL' })
                        }
                        className="rounded border border-border bg-muted px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="AGL">AGL</option>
                        <option value="AMSL">AMSL</option>
                      </select>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => clearSelection()}
                  className="w-full rounded bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground transition hover:bg-destructive/90"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          {/* Tab switcher */}
          <div className="flex bg-card">
            <button
              onClick={() => setActiveTab('zones')}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-xs font-medium transition-colors ${
                activeTab === 'zones'
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Target size={14} />
              Zones
            </button>
            <button
              onClick={() => setActiveTab('criterias')}
              className={`flex flex-1 items-center justify-center gap-2 px-4 py-3 text-xs font-medium transition-colors ${
                activeTab === 'criterias'
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Settings size={14} />
              Criterias
            </button>
          </div>
        </>
      )}

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto p-2 sm:p-3">
        {!showTabs && renderFolderView()}
        {showTabs && activeTab === 'zones' && renderZonesTab()}
        {showTabs && activeTab === 'criterias' && renderCriteriasTab()}
      </div>

    </div>
  );
};

export default UtmSidebar;
